/**
 * Combine all reducers in this file and export the combined reducers.
 */

import { combineReducers, Reducer, createReducer as reducer } from '@reduxjs/toolkit';
import { createReducer as createRouterReducer } from '@okapi/router-connect';
import { createReducer as createCookieReducer } from 'isomorphic-redux-cookie-middleware';
import { GlobalRouter } from '@okapi/router-react';
import type { InjectedReducersType } from 'utils/types/injector-typings';

import { catalogueReducer, catalogueName } from 'containers/catalogue/catalogue';
import {
	popularCategoriesName,
	popularCategoriesReducer,
} from 'containers/catalogue/popular-categories';
import { instoreName, instoreReducer } from 'containers/instore';
import { instoreServiceName, instoreServiceReducer } from 'containers/instore/instore-service';
import {
	configuratorsListingName,
	configuratorsListingReducer,
} from 'containers/configurators-listing';
import { roughWorkName, roughWorkReducer } from 'containers/rough-work/slice';
import { pageStoreReducer } from './pageStore';
import { pageModelReducer } from './pageModel';

export type Env = {
	ORCHESTRATOR_HOST: string;
	USE_CREDENTIALS: boolean;
	requestID: string;
	apiKey?: string;
	ORCHESTRATOR_EXTERNAL_HOST?: string;
	API_KEY_EXTERNAL?: string;
};

const storeReducers = {
	pageStore: pageStoreReducer,
	pageModel: pageModelReducer,
	[catalogueName]: catalogueReducer,
	[popularCategoriesName]: popularCategoriesReducer,
	[instoreName]: instoreReducer,
	[instoreServiceName]: instoreServiceReducer,
	[configuratorsListingName]: configuratorsListingReducer,
	[roughWorkName]: roughWorkReducer,
	// [INSERT NEW STORE REDUCER ABOVE] < Needed for generating slices seamlessly
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
// const defaultReducer: Reducer = state => state;

/**
 * Merges the main reducer with the router state and dynamically injected reducers
 */

type TInitialState = {
	router: GlobalRouter;
	host: string;
	cookie: string;
	isBot: boolean;
	env: Env;
	userAgent: string;
};

export const createReducerWithInitialState = (initialState: TInitialState) => (
	injectedReducers: InjectedReducersType = {}
): ReturnType<typeof combineReducers> | Reducer => {
	const { router, host, isBot, userAgent, env, cookie } = initialState;

	const reducers = {
		spl: combineReducers({ ...storeReducers, ...injectedReducers }),
		router: createRouterReducer(router),
		cookies: createCookieReducer(cookie),
		host: reducer(host, () => host),
		isBot: reducer(isBot, () => isBot),
		userAgent: reducer(userAgent, () => userAgent),
		env: reducer(env, () => env),
	};
	// // Initially we don't have any reducers, so returning identity function to avoid the error
	// if (!Object.keys(reducers).length) {
	// 	return defaultReducer;
	// }
	return combineReducers(reducers);
};
